"use client"

import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Flower, ChevronDown, Menu } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"

// Smooth scroll utility
const smoothScrollTo = (elementId: string) => {
  const element = document.getElementById(elementId)
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

export default function Header() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-purple-950/80 border-b border-purple-800/50">
      <div className="container mx-auto px-4 py-4">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flower className="h-6 w-6 text-purple-300" />
            <span className="text-xl font-bold text-white">Lavender Flow</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <button 
              onClick={() => smoothScrollTo('features')} 
              className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer"
            >
              Features
            </button>
            <button 
              onClick={() => smoothScrollTo('pricing')} 
              className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer"
            >
              Pricing
            </button>
            <Link to="/integrations" className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer">
              Integrations
            </Link>
            <Link to="/help" className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer">
              Help
            </Link>
            <Link to="/help" className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer">
              Community
            </Link>
            <div className="relative group">
              <button className="flex items-center space-x-1 text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer focus:outline-none">
                <span>Gallery</span>
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
              </button>
              {/* Hover Menu */}
              <div className="absolute top-full left-[-60px] mt-2 w-[260px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link 
                    to="/gallery/workflows" 
                    className="block px-4 py-2 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200 cursor-pointer"
                  >
                    <div className="font-medium">Workflow Gallery</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Browse AI automation gallery</div>
                  </Link>
                  <Link 
                    to="/gallery/flows" 
                    className="block px-4 py-2 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200 cursor-pointer"
                  >
                    <div className="font-medium">Flow Store</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Reusable workflow components</div>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation (Hamburger Menu) */}
          <div className="md:hidden flex items-center">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6 text-white" />
                  <span className="sr-only">Toggle navigation</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-purple-900 text-white">
                <SheetHeader>
                  <div className="flex items-center gap-2">
                    <Flower className="h-6 w-6 text-purple-300" />
                    <SheetTitle className="text-white">Lavender Flow</SheetTitle>
                  </div>
                </SheetHeader>
                <div className="flex flex-col gap-4 px-4 py-4">
                  <button 
                    onClick={() => smoothScrollTo('features')} 
                    className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer"
                  >
                    Features
                  </button>
                  <button 
                    onClick={() => smoothScrollTo('pricing')} 
                    className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer"
                  >
                    Pricing
                  </button>
                  <Link to="/integrations" className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer">
                    Integrations
                  </Link>
                  <Link to="/help" className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer">
                    Help
                  </Link>
                  <Link to="/help" className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer">
                    Community
                  </Link>
                  <div className="space-y-2">
                    <div className="text-purple-300 font-medium text-sm uppercase tracking-wide mb-2">Gallery</div>
                    <div className="flex flex-col gap-2 pl-4">
                      <Link
                        to="/gallery/workflows"
                        className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer py-1"
                      >
                        Workflow Gallery
                      </Link>
                      <Link
                        to="/gallery/flows"
                        className="text-purple-200 hover:text-white transition-colors duration-200 cursor-pointer py-1"
                      >
                        Flow Store
                      </Link>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 mt-4">
                    <Link to="/login">
                      <Button variant="ghost" className="w-full text-purple-200 hover:text-white hover:bg-purple-800">
                        Log in
                      </Button>
                    </Link>
                    <Link to="/login">
                      <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white">Sign up</Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Login/Signup Buttons (Desktop) */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-purple-200 hover:text-white hover:bg-purple-800 transition-all duration-200 cursor-pointer">
                Log in
              </Button>
            </Link>
            <Link to="/login">
              <Button className="bg-purple-500 hover:bg-purple-600 text-white transition-all duration-200 cursor-pointer hover:scale-105">Sign up</Button>
            </Link>
          </div>
        </nav>
      </div>
    </header>
  )
}
